import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { authApi, sellerApi } from "../../services/api";
import {
  AuthState,
  LoginCredentials,
  Permission,
  AuthTokens,
} from "../../types/auth.types";

const initialState: AuthState = {
  user: null,
  username: null,
  permissions: [],
  isLoading: false,
  error: null,
  isAuthenticated: false,
  loginInProgress: false,
  requiresOtp: false,
};

const debounceTimeouts = new Map<string, NodeJS.Timeout>();

const debounceThunk = <T>(
  thunkAction: () => Promise<T>,
  delay: number = 500,
  key: string = "default"
): Promise<T> => {
  if (debounceTimeouts.has(key)) {
    clearTimeout(debounceTimeouts.get(key)!);
    debounceTimeouts.delete(key);
  }

  return new Promise<T>((resolve, reject) => {
    const timeoutId = setTimeout(async () => {
      try {
        const result = await thunkAction();
        debounceTimeouts.delete(key);
        resolve(result);
      } catch (error) {
        debounceTimeouts.delete(key);
        reject(error);
      }
    }, delay);

    debounceTimeouts.set(key, timeoutId);
  });
};

export const checkAuth = createAsyncThunk<Permission[]>(
  "auth/checkAuth",
  async (_, { dispatch, rejectWithValue }) => {
    return debounceThunk(async () => {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        authApi.logout();
        const isUserLoggedOut = localStorage.getItem("isUserLoggedOut");
        if (isUserLoggedOut) {
          localStorage.removeItem("isUserLoggedOut");
          throw new Error("Logged out");
        } else {
          throw new Error("No Message");
        }
      } else {
        try {
          const response = await authApi.getUserPermissions();
          if (response.status === "SUCCESS") {
            return response.data;
          } else {
            authApi.logout();
            throw new Error(response.message);
          }
        } catch (error) {
          authApi.logout();
          if (error instanceof Error) {
            throw error;
          }
          throw new Error("Session expired");
        }
      }
    });
  }
);

export const loginUser = createAsyncThunk(
  "auth/login",
  async (tokens: AuthTokens, { rejectWithValue }) => {
    try {
      // Store tokens in localStorage
      localStorage.setItem("accessToken", tokens.accessToken);
      localStorage.setItem("refreshToken", tokens.refreshToken);
      
      // Return success
      return { tokens, requiresOtp: false };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to store tokens");
    }
  }
);

export const fetchUserPermissions = createAsyncThunk(
  "auth/fetchPermissions",
  async (_, { dispatch }) => {
    return debounceThunk(async () => {
      try {
        const response = await authApi.getUserPermissions();
        if (response.status === "SUCCESS") {
          return response.data;
        }
        await dispatch(logoutUser());
        throw new Error(response.message || "Failed to fetch permissions");
      } catch (error) {
        await dispatch(logoutUser());
        throw error;
      }
    });
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { dispatch }) => {
    return debounceThunk(async () => {
      try {
        await authApi.logout();
        return true;
      } catch (error) {
        console.error("Logout error:", error);
        authApi.logout();
        return true;
      }
    });
  }
);

export const fetchUserInfo = createAsyncThunk(
  "auth/fetchUserInfo",
  async (_, { getState }) => {
    return debounceThunk(
      async () => {
        try {
          const { auth } = getState() as { auth: AuthState };
          if (auth.user && Object.keys(auth.user).length) {
            return auth.user;
          }
          const response = await sellerApi.getCurrentUser();
          if (response.status === "SUCCESS") {
            return response.data;
          }
          return null;
        } catch (error) {
          if (error instanceof Error) {
            throw new Error("Failed to fetch user info");
          }
          throw error;
        }
      },
      1000,
      "fetchUserInfo"
    );
  }
);

export const toggle2FA = createAsyncThunk(
  "auth/toggle2FA",
  async (enabled: boolean, { rejectWithValue }) => {
    try {
      const response = await authApi.toggle2FA(enabled);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to toggle 2FA"
      );
    }
  }
);

const setPending = (state: AuthState) => {
  state.isLoading = true;
  state.error = null;
};

const clearAuthState = (state: AuthState) => {
  state.isLoading = false;
  state.isAuthenticated = false;
  state.user = null;
  state.username = null;
  state.permissions = [];
  state.loginInProgress = false;
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetLoginProgress: (state) => {
      state.loginInProgress = false;
    },
    updateTokenBalance: (state, action) => {
      if (state.user) {
        state.user.tokenBalance = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkAuth.pending, setPending)
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.loginInProgress = true;
      })
      .addCase(fetchUserPermissions.pending, setPending)
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissions = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(checkAuth.rejected, (state, action) => {
        clearAuthState(state);
        const message = action.error.message || "Session validation failed";
        if (message !== "No Message") {
          state.error = message;
        }
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        if (action.payload.requiresOtp) {
          state.isLoading = false;
          state.requiresOtp = true;
          state.loginInProgress = true;
        } else {
          state.isLoading = false;
          state.isAuthenticated = true;
          state.loginInProgress = false;
          state.requiresOtp = false;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.loginInProgress = false;
      })
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissions = action.payload;
        state.error = null;
        state.loginInProgress = false;
      })
      .addCase(fetchUserPermissions.rejected, (state, action) => {
        if (
          action.error.message?.includes("Session expired") ||
          action.error.message?.includes("Token") ||
          action.error.message?.includes("authentication")
        ) {
          clearAuthState(state);
        } else {
          state.isLoading = false;
          state.error = action.error.message || "Failed to fetch permissions";
        }
      })
      .addCase(fetchUserInfo.pending, setPending)
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        state.isLoading = false;
        // Only update user if we got data
        if (action.payload) {
          state.user = action.payload;
        }
        state.error = null;
      })
      .addCase(fetchUserInfo.rejected, (state, action) => {
        state.isLoading = false;
        // Don't clear user data on rejection unless explicitly logging out
        if (action.error.message !== "Failed to fetch user info") {
          state.user = null;
        }
        state.error = action.error.message || "Failed to fetch user info";
      })
      .addCase(toggle2FA.pending, (state: AuthState) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggle2FA.fulfilled, (state: AuthState, action) => {
        state.isLoading = false;
        if (state.user) {
          state.user.twoFactorEnabled = action.meta.arg as boolean;
        }
      })
      .addCase(toggle2FA.rejected, (state: AuthState, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addMatcher(
        (action) =>
          action.type.endsWith("/logout/fulfilled") ||
          action.type.endsWith("/logout/rejected"),
        (state) => {
          return { ...initialState, isAuthenticated: false, username: null };
        }
      );
  },
});

export const { clearError, resetLoginProgress, updateTokenBalance } =
  authSlice.actions;
export default authSlice.reducer;
